import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import Header from "../Common/Header";
import Footer from "../Common/Footer";
import { MdCheck } from "react-icons/md";

interface PolicySection {
  id: string;
  title: string;
  content: React.ReactNode;
  subsections?: PolicySection[];
}

const SecurityPrivacyPolicy: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const importantBg = useColorModeValue("red.50", "red.900");
  const footerBg = useColorModeValue("gray.50", "gray.700");

  const policyData: PolicySection[] = [
    {
      id: "1",
      title: "Introduction and Scope",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            This Security & Privacy Policy ("Policy") governs the collection,
            use, disclosure, and protection of information through the QIQ
            Health mobile application ("App," "Service," "Platform") developed
            and operated by VFrequentists LLP ("Company," "We," "Us," "Our").
          </Text>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional User Base
            </Heading>
            <Text mb={3}>
              This App is designed exclusively for use by qualified healthcare
              professionals, including but not limited to:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Licensed physicians and medical doctors
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Registered nurses and nurse practitioners
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Medical coding specialists and certified coders
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Medical billing experts and healthcare revenue cycle
                professionals
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare administrators with clinical oversight
                responsibilities
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Other licensed healthcare practitioners with appropriate
                credentials
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Acknowledgment
            </Heading>
            <Text mb={3}>
              By accessing or using QIQ Health, you ("User," "You," "Your,"
              "Healthcare Professional") represent that you are a qualified
              healthcare professional and acknowledge that you have read,
              understood, and agree to be bound by this Policy and all
              applicable professional standards and regulations.
            </Text>

            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
            >
              <Text fontWeight="bold" color="red.700">
                IMPORTANT: QIQ Health is designed as a professional
                informational and data management tool for healthcare
                professionals. While the App processes and organizes healthcare
                data, all clinical decisions, medical coding determinations, and
                billing practices remain the sole professional responsibility of
                the licensed healthcare professional using the system.
              </Text>
            </Box>
          </Box>
        </VStack>
      ),
    },
    {
      id: "2",
      title: "Data Processing Foundation and Professional Limitations",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              User-Provided Data Processing Framework
            </Heading>
            <Text mb={3}>
              QIQ Health functions exclusively with data that healthcare
              professionals provide directly through the professional interface.
              The App does not:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Generate independent medical advice or clinical recommendations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Create autonomous diagnostic conclusions
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Produce unsupervised treatment protocols
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Synthesize clinical information from unauthorized external
                sources
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Make independent medical predictions or clinical forecasts
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Determine medical coding without professional oversight
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Execute billing decisions without professional validation
              </ListItem>
            </List>

            <Text mt={3}>
              All outputs, clinical summaries, coding suggestions, billing
              calculations, or processed information are derived solely from
              healthcare professional input data using established medical
              algorithms, coding standards (ICD-10, CPT, HCPCS), and billing
              frameworks.
            </Text>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Responsibility and Clinical Oversight
            </Heading>
            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Text fontWeight="bold" color="red.700">
                AS A LICENSED HEALTHCARE PROFESSIONAL, YOU ARE SOLELY AND
                PROFESSIONALLY RESPONSIBLE FOR:
              </Text>
            </Box>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Verifying the clinical accuracy of all data you input into QIQ
                Health
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Exercising independent professional judgment in assessing
                processed information
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Reviewing all generated outputs against your clinical expertise
                before implementation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Making independent medical, coding, and billing decisions within
                your scope of practice
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Ensuring all data entered complies with applicable medical
                standards, HIPAA regulations, and professional licensing
                requirements
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintaining compliance with your professional licensing board
                requirements
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Adhering to institutional policies and procedures where
                applicable
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Validating all medical coding and billing outputs against
                current coding guidelines and payer requirements
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "3",
      title: "Infrastructure Security and HIPAA Compliance",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              HIPAA-Compliant Infrastructure for Healthcare Professionals
            </Heading>
            <Text mb={3}>
              QIQ Health operates on enterprise-grade infrastructure
              specifically designed for healthcare professional use, with
              services provided by third-party vendors that maintain current
              HIPAA compliance certifications:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Cloud Service Providers:
                </Text>{" "}
                AWS HIPAA-eligible services, Microsoft Azure Healthcare APIs,
                Google Cloud Healthcare & Life Sciences
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Data Storage Systems:
                </Text>{" "}
                Encrypted databases with HIPAA-compliant configurations and
                healthcare-specific access controls
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Network Security:
                </Text>{" "}
                TLS 1.3+ encryption for all clinical data transmissions with
                healthcare endpoint validation
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Authentication Systems:
                </Text>{" "}
                Multi-factor authentication, biometric verification, and secure
                credential management designed for healthcare workflows
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Data Protection:
                </Text>{" "}
                Specialized safeguards for Protected Health Information (PHI)
                processing and storage
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Technical Safeguards for Healthcare Data
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical-Grade Encryption:
                </Text>{" "}
                AES-256 encryption for PHI at rest, TLS 1.3+ for clinical data
                in transit
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Access Controls:
                </Text>{" "}
                Role-based permissions aligned with healthcare professional
                hierarchies and scope of practice
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Audit Logging:
                </Text>{" "}
                Comprehensive logging of all PHI access, clinical data
                modifications, and coding/billing activities
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Session Management:
                </Text>{" "}
                Healthcare-appropriate session timeouts and secure
                authentication handling
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Data Integrity:
                </Text>{" "}
                Cryptographic validation and clinical data accuracy verification
                procedures
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Backup and Recovery:
                </Text>{" "}
                HIPAA-compliant backup systems with healthcare data retention
                requirements
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "4",
      title: "Professional Data Collection and Clinical Processing",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Information Collection
            </Heading>
            <Text mb={3}>
              QIQ Health collects only the information that healthcare
              professionals explicitly provide through the professional
              interface:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Clinical data and patient health information entered by licensed
                professionals
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional identifiers and credentials necessary for
                healthcare operations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Medical coding and billing information for revenue cycle
                management
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Usage analytics for clinical workflow improvement (de-identified
                where possible)
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Technical information required for healthcare system operation
                and security
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional licensing and credentialing information as required
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Legal Basis for Healthcare Data Processing
            </Heading>
            <Text mb={3}>
              Under applicable privacy laws and healthcare regulations, we
              process professional healthcare data based on:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Consent:
                </Text>{" "}
                Healthcare professional consent for clinical data processing
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Operations:
                </Text>{" "}
                Legitimate healthcare operations and professional workflow
                support
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Legal Compliance:
                </Text>{" "}
                Meeting HIPAA, state healthcare regulations, and professional
                licensing requirements
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Standards:
                </Text>{" "}
                Supporting compliance with medical coding and billing standards
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "5",
      title: "Healthcare Professional Rights and Clinical Data Management",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Clinical Data Review and Professional Correction Mechanisms
            </Heading>
            <Text mb={3}>
              QIQ Health provides healthcare-specific functionality for
              professional data management:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Real-time Clinical Review:
                </Text>{" "}
                All entered clinical data immediately available for professional
                verification
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Edit Capabilities:
                </Text>{" "}
                Full editing access for licensed professionals to modify
                clinical information
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Version History:
                </Text>{" "}
                Track changes and modifications to clinical data with
                professional attribution
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Medical Coding Validation:
                </Text>{" "}
                Input validation against current ICD-10, CPT, and HCPCS coding
                standards
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Billing Accuracy Checks:
                </Text>{" "}
                Automated validation against current payer requirements and
                billing guidelines
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Control Rights
            </Heading>
            <Text mb={3}>
              As a licensed healthcare professional, you maintain complete
              control over clinical data, including:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Access:
                </Text>{" "}
                View all stored patient and clinical information within your
                scope of practice
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Rectification:
                </Text>{" "}
                Correct any clinically inaccurate or incomplete data
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Data Deletion:
                </Text>{" "}
                Remove clinical data according to professional and regulatory
                requirements
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Data Portability:
                </Text>{" "}
                Export clinical data in standard healthcare formats (HL7, FHIR,
                etc.)
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Processing Restrictions:
                </Text>{" "}
                Limit clinical data processing according to professional
                judgment
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Consent Management:
                </Text>{" "}
                Control consent for clinical data processing and sharing
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "6",
      title: "Professional Disclaimer and Healthcare Professional Liability",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Clinical Decision Support Disclaimer
            </Heading>
            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Text fontWeight="bold" color="red.700">
                QIQ HEALTH PROVIDES CLINICAL DECISION SUPPORT TOOLS FOR
                HEALTHCARE PROFESSIONALS. All clinical information processed
                through QIQ Health is intended to support, not replace,
                professional clinical judgment and decision-making.
              </Text>
            </Box>

            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Text fontWeight="bold" color="red.700">
                AS A HEALTHCARE PROFESSIONAL, YOU ACKNOWLEDGE AND PROFESSIONALLY
                AGREE THAT:
              </Text>
            </Box>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Your independent professional judgment supersedes any
                system-generated suggestions
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Clinical decisions remain within your scope of practice and
                professional responsibility
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Medical coding and billing determinations require your
                professional validation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                QIQ Health serves as a professional tool to support, not direct,
                clinical practice
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                All patient care decisions must align with applicable standards
                of care and professional guidelines
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Limitation of Liability
            </Heading>
            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Text fontWeight="bold" color="red.700">
                TO THE MAXIMUM EXTENT PERMITTED BY HEALTHCARE LAW:
              </Text>
            </Box>
            <Text mb={3}>
              To the maximum extent permitted by law, VFrequentists LLP, its
              affiliates, officers, directors, employees, agents, suppliers, and
              licensors shall{" "}
              <Text as="span" fontWeight="bold">
                not
              </Text>{" "}
              be liable for any indirect, incidental, special, consequential,
              punitive, or exemplary damages, or for any loss of profits,
              revenues, data, goodwill, business opportunities, or other
              intangible losses, whether based on contract, tort, strict
              liability, or any other legal theory, arising from or related to
              your use of or inability to use QIQ Health, including but not
              limited to clinical decisions, medical coding determinations,
              billing practices, or patient care outcomes, even if we have been
              advised of the possibility of such damages.
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                VFrequentists LLP disclaims all liability for clinical
                decisions, medical coding, billing determinations, and patient
                care decisions made by healthcare professionals using QIQ Health
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare professionals assume full professional responsibility
                and liability for validating all clinical outputs, medical
                coding, and billing determinations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                We provide no warranties, express or implied, regarding clinical
                accuracy, medical coding accuracy, billing accuracy, or fitness
                for any particular purpose beyond the professional input
                provided
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Our liability is strictly limited to technical system
                functionality and availability, not clinical decision-making,
                medical coding, billing, or patient care outcomes
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional malpractice, clinical liability, medical coding
                errors, billing errors, and all patient care liability remain
                solely with the licensed healthcare professional
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Our total aggregate liability for any claim arising out of or
                relating to this Policy or QIQ Health shall not exceed the
                greater of (a) the amount you paid to use the Service in the 12
                months preceding the claim or (b) one hundred US dollars (USD
                $100)
              </ListItem>
            </List>
            <Text mt={3}>
              Some jurisdictions do not allow the exclusion or limitation of
              certain damages, so some of the above limitations may not apply to
              you. However, to the fullest extent permitted by applicable law,
              the limitations and exclusions set forth above shall apply.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "7",
      title: "Healthcare Data Sharing and Professional Disclosure",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Healthcare Data Sharing
            </Heading>
            <Text mb={3}>
              VFrequentists LLP does not sell, rent, or trade healthcare
              professional information or clinical data. Information may only be
              shared:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                With explicit written consent from the healthcare professional
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                With HIPAA-compliant healthcare service providers under executed
                BAAs
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                As required by healthcare law, regulatory agencies, or legal
                process
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                To protect against healthcare fraud or security threats
                affecting clinical operations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                For healthcare operations as permitted under HIPAA and
                professional standards
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Integrations
            </Heading>
            <Text mb={3}>
              Any healthcare system integrations or professional services
              connected to QIQ Health:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain current HIPAA compliance certifications and healthcare
                industry standards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Operate under executed Business Associate Agreements with
                healthcare-specific terms
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Provide equivalent or superior clinical data protection
                standards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Allow healthcare professionals to control the scope and extent
                of clinical data sharing
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Support integration with Electronic Health Records (EHR) and
                healthcare information systems
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "8",
      title: "Healthcare Security Incident Response",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Clinical Data Breach Notification
            </Heading>
            <Text mb={3}>
              In the event of a healthcare data security incident affecting QIQ
              Health:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                VFrequentists LLP will investigate and assess the clinical
                impact within 24 hours
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Affected healthcare professionals will be notified within 72
                hours as required by HIPAA
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Appropriate healthcare authorities and regulatory agencies will
                be notified according to requirements
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Detailed incident information and clinical remediation steps
                will be provided
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare institutions and professional licensing boards will
                be notified as required
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Incident Reporting
            </Heading>
            <Text mb={3}>
              If healthcare professionals suspect a security issue affecting
              clinical data:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Contact our healthcare privacy team immediately at
                privacy@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Provide detailed information about the suspected clinical data
                incident
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                We will investigate and respond within 48 hours with
                healthcare-specific protocols
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare professionals will receive updates on investigation
                status and clinical impact assessment
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "9",
      title: "Healthcare Professional Verification and Credentialing",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Verification Requirements
            </Heading>
            <Text mb={3}>
              Access to QIQ Health requires verification of healthcare
              professional credentials:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Current professional license verification
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Scope of practice validation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Institutional credentialing where applicable
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Continuing education and certification maintenance
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional liability insurance verification (where required)
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Ongoing Professional Compliance
            </Heading>
            <Text mb={3}>
              Healthcare professionals using QIQ Health agree to:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain current professional licensure throughout system access
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Notify VFrequentists LLP of any changes in professional status
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Comply with professional continuing education requirements
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Adhere to institutional policies and procedures where applicable
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Report any professional disciplinary actions or license
                restrictions
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "10",
      title: "International Healthcare Professional Use",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            If healthcare professionals access QIQ Health from outside the
            United States:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Clinical data may be transferred to and processed in the United
              States under healthcare data protection standards
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              We ensure appropriate safeguards for international healthcare data
              transfers
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              We comply with applicable international healthcare data protection
              requirements
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Healthcare professionals consent to such transfers through
              professional system use
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Cross-border healthcare compliance requirements must be met by the
              professional user
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "11",
      title: "Policy Updates and Professional Modifications",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Policy Changes
            </Heading>
            <Text mb={3}>
              VFrequentists LLP reserves the right to modify this Policy for
              healthcare operational requirements. Changes will be:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Posted within QIQ Health with prominent notification to
                healthcare professionals
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Effective immediately upon posting unless otherwise specified
                for clinical operations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Communicated via professional email for material changes
                affecting healthcare professional rights
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Archived with version history available upon professional
                request
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Continued Professional Use
            </Heading>
            <Text>
              Continued use of QIQ Health by healthcare professionals after
              policy modifications constitutes professional acceptance of
              updated terms. If healthcare professionals do not agree to
              changes, they must discontinue use and may request clinical data
              deletion consistent with professional documentation requirements.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "12",
      title: "Healthcare Professional Contact Information and Compliance",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Privacy Officer Contact
            </Heading>
            <Text mb={3}>
              For healthcare privacy-related questions, concerns, or
              professional requests:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Privacy Officer:
                </Text>{" "}
                admin@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Email:
                </Text>{" "}
                privacy@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Professional Support:
                </Text>{" "}
                support@weqiq.com
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Regulatory Compliance
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  HIPAA Compliance Officer:
                </Text>{" "}
                admin@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Data Protection Officer:
                </Text>{" "}
                admin@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Regulatory Inquiries:
                </Text>{" "}
                compliance@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Healthcare Professional Licensing Issues:
                </Text>{" "}
                admin@weqiq.com
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "13",
      title:
        "Legal Jurisdiction and Healthcare Professional Dispute Resolution",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            This Policy is governed by the laws of{" "}
            <Text as="span" fontWeight="semibold">
              India
            </Text>
            , without regard to conflict of law principles. Any disputes arising
            under this Policy involving healthcare professionals shall be
            resolved through:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Initial good-faith professional negotiation
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Binding arbitration conducted in{" "}
              <Text as="span" fontWeight="semibold">
                India
              </Text>{" "}
              under the rules of the{" "}
              <Text as="span" fontWeight="semibold">
                Arbitration and Conciliation Act, 2015
              </Text>{" "}
              and administered by the{" "}
              <Text as="span" fontWeight="semibold">
                Indian Council of Arbitration
              </Text>
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              If arbitration is not enforceable, exclusive jurisdiction and
              venue in the courts of{" "}
              <Text as="span" fontWeight="semibold">
                India
              </Text>{" "}
              for matters not subject to arbitration
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              Professional licensing board oversight where applicable
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "14",
      title: "Healthcare Professional Acknowledgment and Clinical Consent",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              BY USING QIQ HEALTH, HEALTHCARE PROFESSIONALS ACKNOWLEDGE THAT:
            </Text>
          </Box>
          <List spacing={2} styleType="decimal">
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They have read and understood this entire Policy as licensed
              healthcare professionals
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They accept full professional responsibility for clinical
              decision-making and patient care
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They understand QIQ Health's role as a clinical decision support
              tool, not a clinical decision maker
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They consent to the healthcare data practices described herein
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They agree to the professional limitation of liability and
              assumption of clinical risk provisions
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They maintain current professional licensure and credentials
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They will use QIQ Health within their professional scope of
              practice
            </ListItem>
            <ListItem>
              <ListIcon as="span" color="blue.500" fontSize="sm">
                •
              </ListIcon>
              They acknowledge their continuing professional responsibilities
              for patient care and clinical outcomes
            </ListItem>
          </List>

          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              FOR HEALTHCARE INSTITUTIONS: If healthcare professionals are using
              QIQ Health within an institutional setting, they acknowledge
              additional institutional responsibilities for clinical governance,
              quality assurance, and regulatory compliance beyond the scope of
              this Policy.
            </Text>
          </Box>
        </VStack>
      ),
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Header minimal={false} />
      <Container maxW="container.xl" mt={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box
            textAlign="center"
            pb={8}
            borderBottom="3px solid"
            borderColor="blue.500"
          >
            <Heading as="h1" size="2xl" color="blue.600" mb={2}>
              Security & Privacy Policy
            </Heading>
            <Heading as="h2" size="lg" color="gray.600" mb={6}>
              QIQ Health by VFrequentists LLP
            </Heading>

            <Box
              bg={headerBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              p={6}
            >
              <VStack spacing={2} align="stretch">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Effective Date:
                  </Text>{" "}
                  January 1, 2025
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Last Updated:
                  </Text>{" "}
                  August 11, 2025
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Policy Content */}
          <Accordion allowMultiple defaultIndex={[0]}>
            {policyData.map((section) => (
              <AccordionItem
                key={section.id}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                mb={4}
              >
                <AccordionButton
                  py={6}
                  px={6}
                  bg={headerBg}
                  _hover={{ bg: "blue.100" }}
                  _expanded={{
                    bg: "blue.200",
                    borderBottom: "2px solid",
                    borderColor: "blue.400",
                  }}
                >
                  <HStack flex="1" textAlign="left" spacing={4}>
                    <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                      {section.id}
                    </Badge>
                    <Text fontWeight="semibold" fontSize="lg">
                      {section.title}
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={8} px={8}>
                  {section.content}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Footer */}
          <Box
            bg={footerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
          >
            <VStack spacing={6} align="stretch">
              <Text fontStyle="italic" color="gray.600">
                This document has been prepared in accordance with HIPAA Privacy
                and Security Rules, state healthcare privacy laws, professional
                licensing requirements, and international healthcare data
                protection standards. This Policy constitutes a legally binding
                agreement between you and VFrequentists LLP.
              </Text>

              <Divider />

              <VStack spacing={2} align="stretch">
                <Text fontWeight="semibold">VFrequentists LLP</Text>
                <Text fontWeight="semibold">Team QIQ Health</Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Document Version:
                  </Text>{" "}
                  1.0
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Status:
                  </Text>{" "}
                  Final Version - Effective as of January 1, 2025
                </Text>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default SecurityPrivacyPolicy;
